import React, { useEffect, useState } from "react";
import { Loader2, UserCheck, ArrowUpRight, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { apiErr } from "../../../lib/apiErr";
import { ROLE_LABEL } from "../PlatformApp";
import { STATUS_NOTES, T_STATUS } from "./supportUtil";

function ActionDialog({ title, icon: Icon, children, onClose, onSubmit, busy, disabled, submitLabel, testid }) {
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  return (
    <div className="dialog-backdrop" onClick={onClose} data-testid={testid}>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="dialog-panel sm:max-w-md" onClick={(e) => e.stopPropagation()}>
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-soft text-brand"><Icon size={18} /></span>
        <h2 className="mt-4 text-base font-bold text-slate-900">{title}</h2>
        <div className="mt-4 space-y-3">{children}</div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-soft rounded-xl px-4 py-2 text-xs font-bold" data-testid={`${testid}-cancel`}>Batal</button>
          <button type="submit" disabled={busy || disabled} className="btn-primary rounded-xl px-4 py-2 text-xs font-bold" data-testid={`${testid}-submit`}>{busy ? <Loader2 size={13} className="animate-spin" /> : submitLabel}</button>
        </div>
      </form>
    </div>
  );
}

const Label = ({ children }) => <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">{children}</span>;

export function useSupportStaff(open) {
  const [staff, setStaff] = useState([]);
  useEffect(() => { if (open) api.get("/platform/support/staff").then((r) => setStaff(r.data.items || r.data || [])).catch(() => setStaff([])); }, [open]);
  return staff;
}

const StaffOption = ({ s }) => <option value={s.id}>{s.name || s.email} · {s.role_label || ROLE_LABEL[s.role] || s.role} · {s.active_tickets ?? 0} tiket aktif</option>;

export function AssignDialog({ ticket, onClose, onDone }) {
  const staff = useSupportStaff(true);
  const [assignee, setAssignee] = useState(ticket.assignee_id || ""); const [note, setNote] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/platform/support/tickets/${ticket.id}/assign`, { assignee_id: assignee || null, ...(note.trim() ? { note: note.trim() } : {}) }); toast.success(assignee ? "Tiket ditugaskan" : "Penugasan dilepas"); onDone(); }
    catch (e) { toast.error(apiErr(e, "Gagal menugaskan")); } finally { setBusy(false); }
  };
  return (
    <ActionDialog title="Tugaskan tiket" icon={UserCheck} onClose={onClose} onSubmit={submit} busy={busy} submitLabel={assignee ? "Tugaskan" : "Lepas penugasan"} testid="assign-dialog">
      <label><Label>Penangan</Label><select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="input-ui mt-1" data-testid="assign-select"><option value="">— Lepas penugasan —</option>{staff.map((s) => <StaffOption key={s.id} s={s} />)}</select><p className="mt-1 text-[11px] text-slate-400">Menugaskan ke L2 Support otomatis menjadikan tiket level 2.</p></label>
      <label><Label>Catatan (opsional)</Label><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="input-ui mt-1" placeholder="Konteks untuk penangan…" data-testid="assign-note" /></label>
    </ActionDialog>
  );
}

export function EscalateDialog({ ticket, onClose, onDone }) {
  const staff = useSupportStaff(true);
  const [assignee, setAssignee] = useState(""); const [note, setNote] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/platform/support/tickets/${ticket.id}/escalate`, { note: note.trim(), ...(assignee ? { assignee_id: assignee } : {}) }); toast.success("Tiket dieskalasi ke L2"); onDone(); }
    catch (e) { toast.error(apiErr(e, "Gagal eskalasi")); } finally { setBusy(false); }
  };
  return (
    <ActionDialog title="Eskalasi ke L2 Support" icon={ArrowUpRight} onClose={onClose} onSubmit={submit} busy={busy} disabled={note.trim().length < 5} submitLabel="Eskalasi" testid="escalate-dialog">
      <label><Label>Catatan eskalasi (wajib, ≥ 5 karakter)</Label><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="input-ui mt-1" placeholder="Apa yang sudah dicoba & mengapa perlu L2…" data-testid="escalate-note" /></label>
      <label><Label>Penerima L2 (opsional)</Label><select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="input-ui mt-1" data-testid="escalate-assignee"><option value="">Semua L2 (antrean level 2)</option>{staff.filter((s) => s.role === "support_l2").map((s) => <StaffOption key={s.id} s={s} />)}</select></label>
    </ActionDialog>
  );
}

export function StatusDialog({ ticket, statuses, onClose, onDone }) {
  const [status, setStatus] = useState(statuses.find((s) => s !== ticket.status) || statuses[0]); const [note, setNote] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/platform/support/tickets/${ticket.id}/status`, { status, ...(note.trim() ? { note: note.trim() } : {}) }); toast.success(`Status → ${T_STATUS[status].label}`); onDone(); }
    catch (e) { toast.error(apiErr(e, "Gagal mengubah status")); } finally { setBusy(false); }
  };
  return (
    <ActionDialog title="Ubah status tiket" icon={RefreshCcw} onClose={onClose} onSubmit={submit} busy={busy} disabled={status === ticket.status} submitLabel="Simpan status" testid="status-dialog">
      <div className="grid grid-cols-2 gap-2" data-testid="status-options">
        {statuses.map((s) => <button type="button" key={s} onClick={() => setStatus(s)} data-testid={`status-opt-${s}`} className={`rounded-xl border px-3 py-2 text-left text-xs transition-colors ${status === s ? "border-brand bg-brand-soft text-slate-900" : "border-line bg-white text-slate-600 hover:border-slate-300"} ${s === ticket.status ? "opacity-40" : ""}`} disabled={s === ticket.status}><p className="font-bold">{T_STATUS[s].label}</p><p className="mt-0.5 text-[10px] text-slate-500">{STATUS_NOTES[s]}</p></button>)}
      </div>
      <label><Label>Catatan (opsional)</Label><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="input-ui mt-1" data-testid="status-note" /></label>
    </ActionDialog>
  );
}
